'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import type { ClaimTypeWithSections, Office } from '@jlg-website/database/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';

const loading = () => <>Loading...</>;

const FaqForm = dynamic(
  () => import('@/components/admin/faq/faq-form').then((mod) => mod.FaqForm),
  { loading }
);

type FaqFormData = {
  claimsTypesWithSections: ClaimTypeWithSections[];
  offices: Office[];
};

export function FaqCreateOption({ onCreate }: { onCreate: () => void }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<FaqFormData | null>(null);

  const handleSave = () => {
    setOpen(false);
    onCreate();
  };

  useEffect(() => {
    if (!open) return;

    const fetchFormData = async () => {
      const data = await fetch('/admin/api/faq/form-data').then((r) => r.json());
      setFormData(data);
    };

    fetchFormData();
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setFormData(null);
      }}
    >
      <DialogTrigger asChild>
        <Button className="w-full">
          <Plus /> <strong>Create FAQ</strong>
        </Button>
      </DialogTrigger>
      <DialogContent className="lg:min-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create FAQ</DialogTitle>
          {formData && (
            <FaqForm
              claimsTypesWithSections={formData.claimsTypesWithSections}
              offices={formData.offices}
              onSave={handleSave}
              customCancelButton={
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
              }
            />
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

export default FaqCreateOption;
